import { useMemo, type FC } from 'react'
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import {
  LoadingLogo,
  labravDefaultThemeV7 as theme,
} from '@labrav/react-components'
import {
  CustomNavButtonProp,
  LayoutWrapper,
  LayoutWrapperActionData,
  LayoutWrapperActions,
  Milestones,
  NoHeaderLayoutWrapper,
  OnboardingWrapper,
  PageNotFound,
  EventScopeProvider,
  RouteApp,
  SelectedCloudIcon,
  WithErrorBoundary,
  useAnyOneOnboardingCompeted,
  useFlyOutOnboardingStatus,
} from '@labrav/common'
import { FlyoutOffers } from '../components/FlyoutOffers'
import { FLYOUT_PRIVATE_OFFERS_LIST_SCOPE } from '../common/utils/hooks/usePrivateOfferCreationTracking'
import { FlyoutChatShell } from './flyoutChat/FlyoutChatShell'
import {
  CONTRACT_CHAT_PROMPTS,
  LISTING_CHAT_PROMPTS,
  METERING_CHAT_PROMPTS,
  OFFER_CHAT_PROMPTS,
  RESELLER_CHAT_PROMPTS,
  REVENUE_CHAT_PROMPTS,
} from './flyoutChat/flyoutChatPrompts'
import { ProductListingV2 } from '../components/ProductListingV2'
import { ContractListingWrapper } from '../components/ContractListing/ContractListingWrapper'
import {
  ResellerAuthorizations,
  ViewReceivedResellerAuthorization,
  ViewSentResellerAuthorization,
} from '../components/ResellerAuthorizations'
import { FreeTrialLabraUI } from '../components/FreeTrialLabraUI'
import { CloudAndProductNameTitle } from '../components/CloudAndProductNameTitle'
import { RevenueInsightsWrapperV2 } from '../components/RevenueInsightsV2/RevenueInsightsWrapperV2'
import { RenewChannelOfferSubscription } from '../components/Subscription/RenewChannelOfferSubscription/RenewChannelOfferSubscription'
import { SubscriptionsList } from '../components/Subscription/SubscriptionsList/SubscriptionsList'
import { AddEditProductListingWrapper } from '../components/AddEditProduct/AddEditProductListingWrapper'
import { DetailProductListing } from '../components/DetailProductListing/DetailProductListing'
import { Buyers } from '../components/Buyers/BuyersOverview'
import { BuyerDetails } from '../components/BuyerDetails/BuyersDetails'
import { BuyerHeader } from '../components/BuyerDetails/BuyerHeader/BuyerHeader'
import { FlyOutStorefrontV2 } from '../components/FlyoutStorefrontV2'
import {
  ButtonCreationFormikWrapper,
  FlyOutStorefrontTemplateWrapper,
} from '../components/FlyOutStorefront'
import { SearchRankingV2 } from '../components/SearchRanking/SearchRanking'
import { FlyOutUsageMetering } from '../components/FlyOutUsageMetering/FlyOutUsageMetering'
import { ViewChannelOffer } from '../components/ChannelOffers'
import { useSelector } from '../store'
import { useActiveProducts } from '../common/utils/hooks/activeProducts'
import { FlyoutProductsWrapper } from '../common/components/FlyoutProductsWrapper'
import { GetFlyoutRoutesWhenNotOnboarded } from './GetFlyoutRoutesWhenNotOnboarded'
import {
  AddEditResellerAuthorizationComponent,
  ChannelOfferEditWrapper,
  CreateEditPrivateOfferWrapper,
  RenewOfferSubscriptionWrapper,
  SubscriptionDetailWrapper,
  ViewPrivateOfferDetails,
} from './routesComponents'

const OFFERS_TITLE = 'Offers'

const FlyoutOffersRoute = () => (
  <EventScopeProvider metadata={FLYOUT_PRIVATE_OFFERS_LIST_SCOPE}>
    <FlyoutOffers />
  </EventScopeProvider>
)

export type FlyoutOnboardingRouteStatus = 'complete' | 'incomplete'

export type FlyoutRouteScreen = FC

const isFectchingAccounts = () => false

export const MarketplaceRoutes = () => {
  const navigate = useNavigate()

  const activeProducts = useActiveProducts()
  const { isAnyOneOnboardingCompeted } =
    useAnyOneOnboardingCompeted(activeProducts)

  const { pathname } = useLocation()

  const { onboardingComplete: flyoutOnboardingComplete } =
    useFlyOutOnboardingStatus()

  const isFlyoutOnboardingRoute =
    pathname.split('/').includes('onboarding') &&
    pathname.split('/').includes('flyout')

  const isFlyoutUserOnboarded = (): boolean =>
    flyoutOnboardingComplete ||
    (isAnyOneOnboardingCompeted && !isFlyoutOnboardingRoute)

  const flyoutOnboardedWithMultiProduct = useSelector<boolean>(
    isFlyoutUserOnboarded
  )

  const rootRedirectTo = flyoutOnboardedWithMultiProduct
    ? '/overview'
    : '/flyout/onboarding'

  const onboardingRoute = flyoutOnboardingComplete ? 'complete' : 'incomplete'

  const customNav = useMemo<CustomNavButtonProp>(
    () => ({
      shouldShowCustomNavButton: true,
    }),
    []
  )
  const flyOutStorefrontTemplateActions: LayoutWrapperActionData[] = [
    {
      key: 'selectedCloud',
      component: <SelectedCloudIcon />,
      text: '',
      type: 'component',
    },
  ]

  return (
    <div data-testid="cmcm-page">
      <WithErrorBoundary boundary="FlyOutRoutes">
        <OnboardingWrapper
          predicateSelector={isFlyoutUserOnboarded}
          loadingSelector={isFectchingAccounts}
          loadingComponent={<LoadingLogo />}
          onboardingComponent={
            /* istanbul ignore next */
            <div data-testid="onboarding">
              <GetFlyoutRoutesWhenNotOnboarded />
            </div>
          }
        >
          <FlyoutProductsWrapper>
            <Routes>
              <Route path="/" element={<Navigate to={rootRedirectTo} />} />
              <Route
                path="onboarding/*"
                element={
                  onboardingRoute === 'complete' ? (
                    <Navigate to="/overview" />
                  ) : (
                    <div data-testid="onboarding">
                      <GetFlyoutRoutesWhenNotOnboarded />
                    </div>
                  )
                }
              />
              <Route
                path=":cloud/:accountId/products-listing"
                element={
                  <FlyoutChatShell
                    pageLabel="Listings"
                    composerPlaceholder="Ask about your Marketplace listings…"
                    prompts={LISTING_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title="Listings"
                      component={ProductListingV2}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                    />
                  </FlyoutChatShell>
                }
              />
              {/* Redirect old route to new pattern - will be handled by ProductListingV2 component */}
              <Route
                path="products-listing"
                element={
                  <FlyoutChatShell
                    pageLabel="Listings"
                    composerPlaceholder="Ask about your Marketplace listings…"
                    prompts={LISTING_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title="Listings"
                      component={ProductListingV2}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                    />
                  </FlyoutChatShell>
                }
              />
              {/* New route for contracts with cloud and accountId */}
              <Route
                path=":cloud/:accountId/products-listing/:productId/contracts"
                element={<ContractListingWrapper />}
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/contracts/:contractId"
                element={<ContractListingWrapper />}
              />
              {/* Backward compatibility */}
              <Route
                path="products-listing/:productId/contracts"
                element={<ContractListingWrapper />}
              />
              <Route
                path="products-listing/:productId/contracts/:contractId"
                element={<ContractListingWrapper />}
              />

              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/products-listing/:productId/private-offers/edit"
                element={<CreateEditPrivateOfferWrapper />}
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/private-offers/:privateOfferId/edit"
                element={<CreateEditPrivateOfferWrapper />}
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/private-offers/:privateOfferId/details"
                element={<ViewPrivateOfferDetails />}
              />

              {/* Backward compatibility */}
              <Route
                path="products-listing/:productId/private-offers/edit"
                element={<CreateEditPrivateOfferWrapper />}
              />
              <Route
                path="products-listing/:productId/private-offers/:privateOfferId/edit"
                element={<CreateEditPrivateOfferWrapper />}
              />
              <Route
                path="products-listing/:productId/private-offers/:privateOfferId/details"
                element={<ViewPrivateOfferDetails />}
              />

              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/products-listing/:productId/resale-authorizations/edit"
                element={<AddEditResellerAuthorizationComponent />}
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/resale-authorizations/:resaleAuthorizationId/edit"
                element={<AddEditResellerAuthorizationComponent />}
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/resale-authorizations/:resaleAuthorizationId/details"
                element={
                  <NoHeaderLayoutWrapper
                    component={ViewSentResellerAuthorization}
                  />
                }
              />

              {/* Backward compatibility */}
              <Route
                path="products-listing/:productId/resale-authorizations/edit"
                element={<AddEditResellerAuthorizationComponent />}
              />

              <Route
                path="products-listing/:productId/resale-authorizations/:resaleAuthorizationId/edit"
                element={<AddEditResellerAuthorizationComponent />}
              />

              <Route
                path="products-listing/:productId/resale-authorizations/:resaleAuthorizationId/details"
                element={
                  <NoHeaderLayoutWrapper
                    component={ViewSentResellerAuthorization}
                  />
                }
              />
              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/resale-authorizations/received/:resaleAuthorizationId/details"
                element={
                  <NoHeaderLayoutWrapper
                    component={ViewReceivedResellerAuthorization}
                  />
                }
              />
              {/* Backward compatibility */}
              <Route
                path="/resale-authorizations/received/:resaleAuthorizationId/details"
                element={
                  <NoHeaderLayoutWrapper
                    component={ViewReceivedResellerAuthorization}
                  />
                }
              />

              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/products-listing/:productId/free-trial"
                element={
                  <RouteApp
                    titleIcon={<LocalOfferOutlined fontSize="medium" />}
                    title="Public Free Trial"
                    component={FreeTrialLabraUI}
                    actions={CloudAndProductNameTitle}
                  />
                }
              />

              {/* Backward compatibility */}
              <Route
                path="products-listing/:productId/free-trial"
                element={
                  <RouteApp
                    titleIcon={<LocalOfferOutlined fontSize="medium" />}
                    title="Public Free Trial"
                    component={FreeTrialLabraUI}
                    actions={CloudAndProductNameTitle}
                  />
                }
              />
              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/reseller-authorizations/*"
                element={
                  <FlyoutChatShell
                    pageLabel="Reseller authorizations"
                    composerPlaceholder="Ask about your reseller agreements…"
                    prompts={RESELLER_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title="Reseller authorizations"
                      component={ResellerAuthorizations}
                      actions={SelectedCloudIcon}
                      isSidePanelOpened={false}
                    />
                  </FlyoutChatShell>
                }
              />
              {/* Backward compatibility */}
              <Route
                path="/reseller-authorizations/*"
                element={
                  <FlyoutChatShell
                    pageLabel="Reseller authorizations"
                    composerPlaceholder="Ask about your reseller agreements…"
                    prompts={RESELLER_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title="Reseller authorizations"
                      component={ResellerAuthorizations}
                      actions={SelectedCloudIcon}
                      isSidePanelOpened={false}
                    />
                  </FlyoutChatShell>
                }
              />
              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/offers-authorizations/*"
                element={
                  <FlyoutChatShell
                    pageLabel="Offers"
                    composerPlaceholder="Ask about your offers…"
                    prompts={OFFER_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title={OFFERS_TITLE}
                      component={FlyoutOffersRoute}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                    />
                  </FlyoutChatShell>
                }
              />
              {/* Backward compatibility */}
              <Route
                path="offers-authorizations/*"
                element={
                  <FlyoutChatShell
                    pageLabel="Offers"
                    composerPlaceholder="Ask about your offers…"
                    prompts={OFFER_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title={OFFERS_TITLE}
                      component={FlyoutOffersRoute}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                    />
                  </FlyoutChatShell>
                }
              />

              {/* New revenue routes with cloud and accountId */}
              <Route
                path=":cloud/:accountId/revenue"
                element={
                  <FlyoutChatShell
                    pageLabel="Revenue insights"
                    composerPlaceholder="Ask about your marketplace revenue…"
                    prompts={REVENUE_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title={'Revenue insights'}
                      component={RevenueInsightsWrapperV2}
                      actions={Milestones}
                      customNavButton={customNav}
                      isSidePanelOpened={false}
                    />
                  </FlyoutChatShell>
                }
              />
              {/* Backward compatibility */}
              <Route
                path="revenue"
                element={
                  <FlyoutChatShell
                    pageLabel="Revenue insights"
                    composerPlaceholder="Ask about your marketplace revenue…"
                    prompts={REVENUE_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title={'Revenue insights'}
                      component={RevenueInsightsWrapperV2}
                      actions={Milestones}
                      customNavButton={customNav}
                      isSidePanelOpened={false}
                    />
                  </FlyoutChatShell>
                }
              />

              {/* New subscription routes with cloud and accountId */}
              <Route
                path=":cloud/:accountId/subscriptions/edit"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewOfferSubscriptionWrapper}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/subscriptions/:subscriptionId/edit"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewOfferSubscriptionWrapper}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/subscriptions/:subscriptionId/edit/:agreementOfferId"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewOfferSubscriptionWrapper}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/subscriptions/:subscriptionId/authorization/:authorizationId/edit"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewChannelOfferSubscription}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/subscriptions/:subscriptionId/authorization/:authorizationId/edit/:agreementOfferId"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewChannelOfferSubscription}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/subscriptions"
                element={
                  <FlyoutChatShell
                    pageLabel="Contracts"
                    composerPlaceholder="Ask about your subscriptions…"
                    prompts={CONTRACT_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title="Contracts"
                      component={SubscriptionsList}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                    />
                  </FlyoutChatShell>
                }
              />
              <Route
                path=":cloud/:accountId/subscriptions/:subscriptionsId/detail"
                element={
                  <NoHeaderLayoutWrapper
                    component={SubscriptionDetailWrapper}
                    isSidePanelOpened={false}
                  />
                }
              />

              {/* Backward compatibility routes */}
              <Route
                path="subscriptions/edit"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewOfferSubscriptionWrapper}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path="subscriptions/:subscriptionId/edit"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewOfferSubscriptionWrapper}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path="subscriptions/:subscriptionId/edit/:agreementOfferId"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewOfferSubscriptionWrapper}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path="subscriptions/:subscriptionId/authorization/:authorizationId/edit"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewChannelOfferSubscription}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path="subscriptions/:subscriptionId/authorization/:authorizationId/edit/:agreementOfferId"
                element={
                  <LayoutWrapper
                    title="Create agreement based offer"
                    component={RenewChannelOfferSubscription}
                    isBack={true}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path="subscriptions"
                element={
                  <FlyoutChatShell
                    pageLabel="Contracts"
                    composerPlaceholder="Ask about your subscriptions…"
                    prompts={CONTRACT_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title="Contracts"
                      component={SubscriptionsList}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                    />
                  </FlyoutChatShell>
                }
              />
              <Route
                path="subscriptions/:subscriptionsId/detail"
                element={
                  <NoHeaderLayoutWrapper
                    component={SubscriptionDetailWrapper}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/products-listing/edit"
                element={
                  <NoHeaderLayoutWrapper
                    component={AddEditProductListingWrapper}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              {/* Backward compatibility */}
              <Route
                path="products-listing/edit"
                element={
                  <NoHeaderLayoutWrapper
                    component={AddEditProductListingWrapper}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/edit"
                element={
                  <NoHeaderLayoutWrapper
                    component={AddEditProductListingWrapper}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              {/* Backward compatibility */}
              <Route
                path="products-listing/:productId/edit"
                element={
                  <NoHeaderLayoutWrapper
                    component={AddEditProductListingWrapper}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              <Route
                path=":cloud/:accountId/products-listing/:productId/details"
                element={
                  <NoHeaderLayoutWrapper
                    component={DetailProductListing}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              {/* Backward compatibility */}
              <Route
                path="products-listing/:productId/details"
                element={
                  <NoHeaderLayoutWrapper
                    component={DetailProductListing}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                  />
                }
              />
              {/* New route with fixed aws and accountId */}
              <Route
                path="aws/:accountId/buyers"
                element={
                  <LayoutWrapper
                    title={'Buyers'}
                    component={Buyers}
                    isSidePanelOpened={false}
                    actions={Milestones}
                    customNavButton={customNav}
                    bgColor={theme.palette.labraV2BoxShadow.dark}
                  />
                }
              />
              <Route
                path="aws/:accountId/buyers/:buyerId"
                element={
                  <LayoutWrapper
                    title={<BuyerHeader />}
                    component={BuyerDetails}
                    isSidePanelOpened={false}
                    actions={Milestones}
                    customNavButton={customNav}
                    bgColor={theme.palette.labraV2BoxShadow.dark}
                  />
                }
              />
              {/* Backward compatibility */}
              <Route
                path="buyers"
                element={
                  <LayoutWrapper
                    title={'Buyers'}
                    component={Buyers}
                    isSidePanelOpened={false}
                    actions={Milestones}
                    customNavButton={customNav}
                    bgColor={theme.palette.labraV2BoxShadow.dark}
                  />
                }
              />
              <Route
                path="buyers/:buyerId"
                element={
                  <LayoutWrapper
                    title={<BuyerHeader />}
                    component={BuyerDetails}
                    isSidePanelOpened={false}
                    actions={Milestones}
                    customNavButton={customNav}
                    bgColor={theme.palette.labraV2BoxShadow.dark}
                  />
                }
              />
              <Route
                path="storefront/*"
                element={
                  <LayoutWrapper
                    title={'Studio'}
                    component={FlyOutStorefrontV2}
                    isSidePanelOpened={false}
                    actions={Milestones}
                    customNavButton={customNav}
                    bgColor={theme.palette.backgroundV2.light}
                  />
                }
              />

              <Route
                path="/storefront/builder/listing-card"
                element={
                  <LayoutWrapper
                    title={'Create new card design'}
                    component={FlyOutStorefrontTemplateWrapper}
                    isSidePanelOpened={false}
                    isBack={true}
                    actions={() => (
                      <LayoutWrapperActions
                        actions={flyOutStorefrontTemplateActions}
                      />
                    )}
                    customNavButton={customNav}
                    onBack={() => navigate('storefront')}
                  />
                }
              />

              <Route
                path="/storefront/builder/buttons"
                element={
                  <LayoutWrapper
                    title={'Create new button'}
                    component={ButtonCreationFormikWrapper}
                    isSidePanelOpened={false}
                    isBack={true}
                    actions={() => (
                      <LayoutWrapperActions
                        actions={flyOutStorefrontTemplateActions}
                      />
                    )}
                    customNavButton={customNav}
                    onBack={() => navigate('storefront/builder')}
                  />
                }
              />
              <Route
                path="/storefront/builder/buttons/:integrationId"
                element={
                  <LayoutWrapper
                    title={'Edit button'}
                    component={ButtonCreationFormikWrapper}
                    isSidePanelOpened={false}
                    isBack={true}
                    customNavButton={customNav}
                    actions={() => (
                      <LayoutWrapperActions
                        actions={flyOutStorefrontTemplateActions}
                      />
                    )}
                    onBack={() => navigate('storefront/builder')}
                  />
                }
              />

              <Route
                path={'/storefront/builder/listing-card/:listingCardId'}
                element={
                  <LayoutWrapper
                    title={'Create new card design'}
                    component={FlyOutStorefrontTemplateWrapper}
                    isSidePanelOpened={false}
                    isBack={true}
                    actions={() => (
                      <LayoutWrapperActions
                        actions={flyOutStorefrontTemplateActions}
                      />
                    )}
                    customNavButton={customNav}
                    onBack={() => navigate('storefront')}
                  />
                }
              />
              <Route
                path="search-ranking"
                element={
                  <LayoutWrapper
                    title={'Search ranking detail'}
                    component={SearchRankingV2}
                    isSidePanelOpened={false}
                    actions={Milestones}
                    customNavButton={customNav}
                    bgColor={theme.palette.bg.main}
                  />
                }
              />
              {/* New route with cloud and accountId */}
              <Route
                path=":cloud/:accountId/usage-metering"
                element={
                  <FlyoutChatShell
                    pageLabel="Usage metering"
                    composerPlaceholder="Ask about your metering records…"
                    prompts={METERING_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title={'Usage metering'}
                      component={FlyOutUsageMetering}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                      bgColor={theme.palette.backgroundV2.light}
                      paddingZero
                    />
                  </FlyoutChatShell>
                }
              />
              {/* Backward compatibility */}
              <Route
                path="usage-metering"
                element={
                  <FlyoutChatShell
                    pageLabel="Usage metering"
                    composerPlaceholder="Ask about your metering records…"
                    prompts={METERING_CHAT_PROMPTS}
                  >
                    <LayoutWrapper
                      title={'Usage metering'}
                      component={FlyOutUsageMetering}
                      isSidePanelOpened={false}
                      actions={Milestones}
                      customNavButton={customNav}
                      bgColor={theme.palette.backgroundV2.light}
                    />
                  </FlyoutChatShell>
                }
              />

              {/* New routes with cloud and accountId */}
              <Route
                path=":cloud/:accountId/authorization/:authorizationId/channel-offers/edit"
                element={<ChannelOfferEditWrapper />}
              />
              <Route
                path=":cloud/:accountId/authorization/:authorizationId/channel-offers/:channelOfferId/edit"
                element={<ChannelOfferEditWrapper />}
              />
              <Route
                path=":cloud/:accountId/authorization/:authorizationId/channel-offers/:channelOfferId/detail"
                element={
                  <NoHeaderLayoutWrapper
                    component={ViewChannelOffer}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                    isBack
                  />
                }
              />

              {/* Backward compatibility routes */}
              <Route
                path="authorization/:authorizationId/channel-offers/edit"
                element={<ChannelOfferEditWrapper />}
              />
              <Route
                path="authorization/:authorizationId/channel-offers/:channelOfferId/edit"
                element={<ChannelOfferEditWrapper />}
              />
              <Route
                path="authorization/:authorizationId/channel-offers/:channelOfferId/detail"
                element={
                  <NoHeaderLayoutWrapper
                    component={ViewChannelOffer}
                    actions={SelectedCloudIcon}
                    isSidePanelOpened={false}
                    isBack
                  />
                }
              />

              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </FlyoutProductsWrapper>
        </OnboardingWrapper>
      </WithErrorBoundary>
    </div>
  )
}
